import { BreadcrumbList } from '@/components/Breadcrumbs/BreadcrumbList';
import { BreadcrumbItem } from '@/components/Breadcrumbs/BreadcrumbItem';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '@/components/Loading/Loader';
import { NotFound } from '@/components/Message/NotFound';
import { EmailTemplateInfosManagement } from '../components/EmailTemplateInfosManagement';
import {
  TEmailTemplateSchema,
  emailTemplateSchema,
} from '@/schemas/email-template/email-template.schema';
import {
  useGetEmailTemplateById,
  useUpdateEmailTemplate,
} from '@/hooks/email-template.hooks';
import { useTranslation } from 'react-i18next';
import { getLanguage } from '@/utils/language.utils';

export const EditEmailTemplatePage = () => {
  const { id } = useParams();
  const { data, isLoading } = useGetEmailTemplateById(`${id}`);
  const { t } = useTranslation('emailTemplate');
  const navigate = useNavigate();

  const {
    watch: watchEmailTemplate,
    control: emailTemplateControl,
    register: registerEmailTemplate,
    setValue: setEmailTemplateValue,
    setError: setEmailTemplateError,
    handleSubmit: handleSubmitEmailTemplate,
    formState: {
      errors: emailTemplateErrors,
      isSubmitting: isSubmittingEmailTemplate,
    },
  } = useForm<TEmailTemplateSchema>({
    resolver: zodResolver(emailTemplateSchema),
  });

  const {
    mutate: mutateUpdateEmailTemplate,
    isPending: isPendingUpdateEmailTemplate,
  } = useUpdateEmailTemplate({
    t,
    handleSuccess: () => navigate('/email-templates'),
  });

  const onSubmitEmailTemplateInfos = (emailTemplate: TEmailTemplateSchema) => {
    const { languageCode } = getLanguage();

    const dataWithLanguageCode = {
      ...emailTemplate,
      languageCode,
    };

    const emailTemplateWithId = { id, ...dataWithLanguageCode };
    mutateUpdateEmailTemplate(emailTemplateWithId);
  };

  return (
    <>
      <PageHeader
        backToLink={history?.length > 1 ? -1 : '/email-templates'}
        title={t('theBrevoEmailTemplate')}
        subTitle={data?.data?.name}
        subTitleIconClassName="ki-paper-plane"
        headingElement={
          <BreadcrumbList>
            <BreadcrumbItem
              text={t('emailTemplateList')}
              linkTo="/email-templates"
            />
            <BreadcrumbItem text={data?.data?.name} active />
          </BreadcrumbList>
        }
      />
      {!isLoading && !data?.data ? (
        <NotFound text={`${t('emailTemplateNotFound')}...`} />
      ) : (
        <div>
          <EmailTemplateInfosManagement<TEmailTemplateSchema>
            t={t}
            data={!isLoading && data?.data}
            watch={watchEmailTemplate}
            control={emailTemplateControl}
            register={registerEmailTemplate}
            setValue={setEmailTemplateValue}
            setError={setEmailTemplateError}
            errors={emailTemplateErrors}
            submitDisabled={
              isSubmittingEmailTemplate || isPendingUpdateEmailTemplate
            }
            onSubmit={handleSubmitEmailTemplate(onSubmitEmailTemplateInfos)}
          />
        </div>
      )}
      {(isLoading ||
        isSubmittingEmailTemplate ||
        isPendingUpdateEmailTemplate) && <Loader />}
    </>
  );
};
